import React, { useCallback, useState, useEffect } from 'react';
import { DataDisplayTable } from "components/data-display";
import { DateToLocal } from "components/dates";
import { useSelector } from 'react-redux';
import { ListingTable } from "components/listing";
import { Button } from "components/buttons";
import moment from 'moment';

export function DetailsListing({ onClick }) {
    const [loading, setLoading] = useState(true);
    const defaultRecordSize = 25;
    const tableHeaders = ["ID/Registration", "Tyres Checked", "Status", "Defects", "View Check"];
    const noDataMessage = "No Jobs found.";
    const [vehiclesWithDefectsRows, setVehiclesWithDefectsRows] = useState([]);
    const [allVehicleRows, setAllVehicleRows] = useState([]);
    const [tableSortBy, setTableSortBy] = useState({ headerName: "ID/Registration", sortName: "REGISTRATION" });
    const [defectTableSortBy, setDefectTableSortBy] = useState({ headerName: "ID/Registration", sortName: "REGISTRATION" });
    const [defecttableSortDir, setDefectTableSortDir] = useState("D");
    const [tableSortDir, setTableSortDir] = useState("D");
    const [vehicleRecordCount, setVehicleRecordCount] = useState(0);
    const [defectRecordCount, setDefectRecordCount] = useState(0);
    const [headerSections, setHeaderSections] = useState([]);
    const [defectsRaised, setDefectsRaised] = useState(null);
    const [tyreCount, setTyreCount] = useState(0);
    const [pageNumber, setPageNumber] = useState(1);
    const [defectPageNumber, setDefectPageNumber] = useState(1);
    const tyreCheckData = useSelector(state => state.tyreCheck);
    const columnsPerRow = 4;
    const [earliestStartTime, setEarliestStartTime] = useState(null);
    const [duration, setDuration] = useState(null);

    const handleClick = useCallback((orderId, assetId) => {
        onClick(orderId, assetId);
    }, [onClick]);

    const setupVehicleRows = useCallback(() => {
        const allVehicles = [];
        const vehiclesWithDefects = [];

        tyreCheckData.orders.forEach(order => {
            const defects = [];
            let allSignedOff = true;

            order.positions.forEach(position => {
                position.damages?.forEach(damage => {
                    defects.push(damage.damage);
                    if (!damage.signedOffBy) {
                        allSignedOff = false;
                    }
                });
            });

            let status;
            if (defects.length === 0) {
                status = "Pass";
            } else if (allSignedOff) {
                status = (
                    <span className='text-intent-success'>
                        Signed-Off
                    </span>
                );
            } else {
                status = (
                    <span className='text-intent-danger'>
                        Fail
                    </span>
                );
            }

            const row = [
                order.registration,
                order.positions.length,
                status,
                defects.length > 0 ? (
                    <span className='text-intent-danger'>{defects.length}</span>
                ) : (
                    defects
                ),
                <Button
                    key={`view-button-${order.registration}`}
                    intent="primary"
                    text="VIEW"
                    id={`button-view-defects-${order.registration}`}
                    onClick={() => handleClick(order.orderId, order.assetId)}
                    small={true}
                />,
            ];

            allVehicles.push(row);
            if (defects.length > 0) {
                vehiclesWithDefects.push(row);
            }
        });

        setVehicleRecordCount(allVehicles.length);
        setDefectRecordCount(vehiclesWithDefects.length);

        // Pagination logic
        const startIndex = (pageNumber - 1) * defaultRecordSize;
        const endIndex = startIndex + defaultRecordSize;

        const defectStartIndex = (defectPageNumber - 1) * defaultRecordSize;
        const defectEndIndex = defectStartIndex + defaultRecordSize;

        setAllVehicleRows(allVehicles.slice(startIndex, endIndex));
        setVehiclesWithDefectsRows(vehiclesWithDefects.slice(defectStartIndex, defectEndIndex));
    }, [tyreCheckData, handleClick, defectPageNumber, pageNumber]);

    useEffect(() => {
        if (tyreCheckData && tyreCheckData.orders && tyreCheckData.orders.length > 0) {
            // Calculate new values
            let damageCount = 0; 
            damageCount = tyreCheckData.orders.reduce((acc, order) => {
                const damages = order.positions?.flatMap(position =>
                    (position.damages || [])
                ) || [];
                return acc + damages.length;
            }, 0);

            let positionCount = 0;
            positionCount = tyreCheckData.orders.reduce((acc, order) => {
                return acc + (order.positions?.length || 0);
            }, 0);

            // Only update state if values have changed
            setTyreCount(prev => prev !== positionCount ? positionCount : prev);
            setDefectsRaised(prev => prev !== damageCount ? damageCount : prev);

            // Setup vehicle rows
            setupVehicleRows();

            let startTime = tyreCheckData.flsArrivalDate;
            if (startTime) {
                startTime = moment(tyreCheckData.flsArrivalDate);
            }
            else {
                startTime = moment(tyreCheckData.orders.reduce((earliest, current) => {
                    const currentTime = new Date(current.orderStartTime);
                    const earliestTime = new Date(earliest.orderStartTime);
                    return currentTime < earliestTime ? current : earliest;
                }).orderStartTime);
            }

            const endTime = moment(tyreCheckData.orders.reduce((latest, current) => {
                const currentTime = new Date(current.orderEndTime);
                const latestTime = new Date(latest.orderEndTime);
                return currentTime > latestTime ? current : latest;
            }).orderEndTime);

            let flsDuration = tyreCheckData.flsCallDuration;

            if (flsDuration) {
                setDuration(moment.duration(flsDuration, 'minutes'));
            }
            else {
                setDuration(moment.duration(endTime.diff(startTime)));
            }
            setEarliestStartTime(startTime);

            setLoading(false);
        }
    }, [tyreCheckData]);

    const formatDuration = () => {
        if (!duration) return '0m';

        const dur = moment.duration(duration); // duration in milliseconds
        const totalHours = Math.floor(dur.asHours()); // includes days converted to hours
        const minutes = dur.minutes();

        return `${totalHours}:${minutes.toString().padStart(2, '0')} minutes`;
    };

    const setUpHeaders = useCallback(() => {
        if (!tyreCheckData) {
            return [];
        }

        return [
            {
                header: "Job Number",
                value: tyreCheckData.jobId,
                key: "job_number",
            },
            {
                header: "Auditor Name",
                value: tyreCheckData.auditorName,
                key: "auditor_name",
            },
            {
                header: "Check Location",
                value: tyreCheckData.locationName,
                key: "location_name",
            },
            {
                header: "Start Date and Time",
                value: (
                    <DateToLocal format="DD/MM/YYYY HH:mm">
                        {earliestStartTime}
                    </DateToLocal>
                ),
                key: "start-date",
            },
            {
                header: "Duration",
                value: formatDuration(),
                key: "duration",
            },
            {
                header: "Vehicles Checked",
                value: tyreCheckData.orders?.length ?? 0,
                key: "vehicles-checked",
            },
            {
                header: "Tyres Checked",
                value: tyreCount ?? 0,
                key: "tyres-checked",
            },
            {
                header: "Defects Raised",
                value: defectsRaised,
                key: "defects-raised",
            },
        ];
    }, [tyreCheckData, defectsRaised, tyreCount, earliestStartTime, duration]);

    useEffect(() => {
        const newHeaders = setUpHeaders();
        if (JSON.stringify(headerSections) !== JSON.stringify(newHeaders)) {
            setHeaderSections(newHeaders);
        }
    }, [tyreCheckData, defectsRaised, tyreCount, headerSections, setUpHeaders]);

    function onPaginationChange(page) {
        if (page !== pageNumber && page > 0 && page <= Math.ceil(vehicleRecordCount / defaultRecordSize)) {
            setPageNumber(page);
        }
    }

    function onDefectPaginationChange(page) {
        if (page !== defectPageNumber && page > 0 && page <= Math.ceil(defectRecordCount / defaultRecordSize)) {
            setDefectPageNumber(page);
        }
    }

    function onTableSort(header, direction) {
        setTableSortBy(header);
        setTableSortDir(direction);
    }

    function onDefectTableSort(header, direction) {
        setDefectTableSortBy(header);
        setDefectTableSortDir(direction);
    }

    return (
        <div className="container">
            <h3 className="spacer-top">Details</h3>
            <DataDisplayTable
                data={headerSections}
                displayInRows={false}
                loading={loading}
                columnsPerRow={columnsPerRow}
            />
            <h3 className="spacer-top">Vehicles With Tyre Defects</h3>
            <ListingTable
                id="listing-table-defects"
                headers={tableHeaders}
                data={vehiclesWithDefectsRows}
                totalRecordCount={defectRecordCount}
                onPagingChange={onDefectPaginationChange}
                loadingData={loading}
                noDataMessage={noDataMessage}
                sortedBy={defectTableSortBy}
                sortedDir={defecttableSortDir}
                onSort={onDefectTableSort}
                showPerPage={false}
                pageable={true}
                pageSize={defaultRecordSize}
            />
            <h3 className="spacer-top">All Vehicles Checked</h3>
            <ListingTable
                id="listing-table-all"
                headers={tableHeaders}
                data={allVehicleRows}
                totalRecordCount={vehicleRecordCount}
                onPagingChange={onPaginationChange}
                loadingData={loading}
                noDataMessage={noDataMessage}
                sortedBy={tableSortBy}
                sortedDir={tableSortDir}
                onSort={onTableSort}
                showPerPage={false}
                pageable={true}
                pageSize={defaultRecordSize}
            />
        </div>
    );
}